import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import TrustModuleNav from '../../components/trust/TrustModuleNav';

const TrustTransactions = () => {
    const [searchParams] = useSearchParams();
    const [transactions, setTransactions] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showVoidDialog, setShowVoidDialog] = useState(null);
    const [voidingId, setVoidingId] = useState(null);
    const [toast, setToast] = useState(null);
    const [filters, setFilters] = useState({
        account: searchParams.get('account') || '',
        type: '',
        start_date: '',
        end_date: ''
    });

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 4000);
    };

    const [formData, setFormData] = useState({
        account: '',
        client: '',
        matter: '',
        transaction_type: 'DEPOSIT',
        amount: '',
        description: '',
        payee_payor: '',
        check_number: '',
        transaction_date: new Date().toISOString().split('T')[0]
    });

    const [clients, setClients] = useState([]);
    const [matters, setMatters] = useState([]);

    useEffect(() => {
        fetchTransactions();
        fetchAccounts();
        fetchClients();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchTransactions = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            let url = '/api/trust/transactions/';

            const params = new URLSearchParams();
            if (filters.account) params.append('account', filters.account);
            if (filters.type) params.append('type', filters.type);
            if (filters.start_date) params.append('start_date', filters.start_date);
            if (filters.end_date) params.append('end_date', filters.end_date);

            if (params.toString()) url += '?' + params.toString();

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) throw new Error('Failed to fetch transactions');

            const data = await response.json();
            setTransactions(Array.isArray(data) ? data : data.results || []);
        } catch (err) {
            setError(err.message);
            console.error('Error fetching transactions:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchAccounts = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/trust/accounts/', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setAccounts(Array.isArray(data) ? data : data.results || []);
        } catch (err) {
            console.error('Error fetching accounts:', err);
        }
    };

    const fetchClients = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/clients/', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setClients(Array.isArray(data) ? data : data.results || []);
        } catch (err) {
            console.error('Error fetching clients:', err);
        }
    };

    const fetchMatters = async (clientId) => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`/api/cases/?client=${clientId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setMatters(Array.isArray(data) ? data : data.results || []);
        } catch (err) {
            console.error('Error fetching matters:', err);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/trust/transactions/', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.amount?.[0] || errorData.detail || 'Failed to create transaction');
            }

            setShowModal(false);
            setFormData({
                account: '',
                client: '',
                matter: '',
                transaction_type: 'DEPOSIT',
                amount: '',
                description: '',
                payee_payor: '',
                check_number: '',
                transaction_date: new Date().toISOString().split('T')[0]
            });
            showToast('Transaction recorded successfully');
            fetchTransactions();
        } catch (err) {
            showToast(err.message, 'error');
        }
    };

    const handleVoidTransaction = async (txnId) => {
        setVoidingId(txnId);
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`/api/trust/transactions/${txnId}/void/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Failed to void transaction');
            }
            const data = await response.json();
            showToast(`Transaction voided. Reversal: ${data.reversal_reference}`);
            setShowVoidDialog(null);
            fetchTransactions();
        } catch (err) {
            showToast(err.message, 'error');
        } finally {
            setVoidingId(null);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    const transactionTypes = [
        { value: 'DEPOSIT', label: 'Client Deposit' },
        { value: 'WITHDRAWAL', label: 'Client Withdrawal' },
        { value: 'FEE_TRANSFER', label: 'Transfer to Operating' },
        { value: 'REFUND', label: 'Client Refund' },
        { value: 'INTEREST', label: 'Interest Earned' },
        { value: 'TRANSFER_IN', label: 'Transfer from Another Trust' },
        { value: 'TRANSFER_OUT', label: 'Transfer to Another Trust' }
    ];

    if (loading) {
        return (
            <div className="p-6 bg-gray-50 min-h-screen">
                <div className="mb-6 flex items-center justify-between">
                    <div>
                        <div className="h-4 w-40 bg-gray-200 rounded animate-pulse mb-3"></div>
                        <div className="h-7 w-48 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                    <div className="h-10 w-36 bg-gray-200 rounded-lg animate-pulse"></div>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
                    <div className="grid grid-cols-4 gap-4">
                        {[1, 2, 3, 4].map(i => <div key={i} className="h-10 bg-gray-100 rounded-lg animate-pulse"></div>)}
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    {[1, 2, 3, 4, 5].map(i => (
                        <div key={i} className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="h-4 w-20 bg-gray-100 rounded animate-pulse"></div>
                                <div className="h-4 w-24 bg-gray-100 rounded animate-pulse"></div>
                                <div className="h-4 w-28 bg-gray-200 rounded animate-pulse"></div>
                            </div>
                            <div className="h-4 w-20 bg-gray-100 rounded animate-pulse"></div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error && transactions.length === 0) {
        return (
            <div className="p-6 bg-gray-50 min-h-screen">
                <div className="mb-6">
                    <nav className="text-sm text-gray-500 mb-2">
                        <Link to="/trust" className="hover:text-blue-600">Trust Accounts</Link>
                        <span className="mx-2">›</span>
                        <span>Transactions</span>
                    </nav>
                    <h1 className="text-2xl font-bold text-gray-900">Trust Transactions</h1>
                </div>
                <div className="max-w-md mx-auto mt-12">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center">
                        <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                            </svg>
                        </div>
                        <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Transactions</h2>
                        <p className="text-gray-500 mb-6">We couldn't connect to the server. Please try again.</p>
                        <button
                            onClick={() => { setLoading(true); setError(null); fetchTransactions(); }}
                            className="inline-flex items-center px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                        >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {/* Toast */}
            {toast && (
                <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 p-4 rounded-lg border shadow-lg max-w-sm transition-all ${toast.type === 'error' ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                    {toast.type === 'error' ? (
                        <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    ) : (
                        <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    )}
                    <p className="text-sm text-gray-700">{toast.message}</p>
                </div>
            )}

            {/* Header */}
            <div className="mb-6 flex items-center justify-between">
                <div>
                    <nav className="text-sm text-gray-500 mb-2">
                        <Link to="/trust" className="hover:text-blue-600">Trust Accounts</Link>
                        <span className="mx-2">›</span>
                        <span>Transactions</span>
                    </nav>
                    <h1 className="text-2xl font-bold text-gray-900">Trust Transactions</h1>
                </div>
                <button
                    onClick={() => setShowModal(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center font-medium transition-colors"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    New Transaction
                </button>
            </div>

            {/* Trust module sub-navigation */}
            <TrustModuleNav />

            {/* Summary stats bar */}
            {transactions.length > 0 && (() => {
                const totalCredits = transactions.filter(t => t.is_credit && t.status !== 'VOID').reduce((s, t) => s + parseFloat(t.amount || 0), 0);
                const totalDebits  = transactions.filter(t => !t.is_credit && t.status !== 'VOID').reduce((s, t) => s + parseFloat(t.amount || 0), 0);
                const voidCount    = transactions.filter(t => t.status === 'VOID').length;
                return (
                    <div className="grid grid-cols-3 gap-4 mb-5">
                        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex items-center gap-3">
                            <div className="p-2 bg-green-100 rounded-lg">
                                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-xs text-green-700 font-medium">Total Credits</p>
                                <p className="text-lg font-bold text-green-800">{formatCurrency(totalCredits)}</p>
                            </div>
                        </div>
                        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center gap-3">
                            <div className="p-2 bg-red-100 rounded-lg">
                                <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 13l-5 5m0 0l-5-5m5 5V6" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-xs text-red-700 font-medium">Total Debits</p>
                                <p className="text-lg font-bold text-red-800">{formatCurrency(totalDebits)}</p>
                            </div>
                        </div>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 flex items-center gap-3">
                            <div className="p-2 bg-gray-100 rounded-lg">
                                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-xs text-gray-600 font-medium">Voided</p>
                                <p className="text-lg font-bold text-gray-700">{voidCount} transaction{voidCount !== 1 ? 's' : ''}</p>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Account</label>
                        <select
                            value={filters.account}
                            onChange={(e) => setFilters({ ...filters, account: e.target.value })}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        >
                            <option value="">All Accounts</option>
                            {accounts.map(acc => (
                                <option key={acc.id} value={acc.id}>{acc.account_name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                        <select
                            value={filters.type}
                            onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        >
                            <option value="">All Types</option>
                            {transactionTypes.map(type => (
                                <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                        <input
                            type="date"
                            value={filters.start_date}
                            onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                        <input
                            type="date"
                            value={filters.end_date}
                            onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        />
                    </div>
                </div>
                <div className="mt-4 flex justify-end">
                    <button
                        onClick={fetchTransactions}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                        Apply Filters
                    </button>
                </div>
            </div>

            {/* Transactions Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Date
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Reference
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Client
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Type
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Description
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Amount
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Balance
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {transactions.length > 0 ? (
                            transactions.map((txn) => (
                                <tr key={txn.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {txn.transaction_date}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600">
                                        {txn.reference_number}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {txn.client_name}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${txn.is_credit ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                            }`}>
                                            {txn.transaction_type_display}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                                        {txn.description}
                                    </td>
                                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-medium ${txn.is_credit ? 'text-green-600' : 'text-red-600'
                                        }`}>
                                        {txn.is_credit ? '+' : '-'}{formatCurrency(txn.amount)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                                        {formatCurrency(txn.running_balance)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                        <span className={`inline-flex px-2 py-0.5 text-xs rounded-full font-medium ${
                                            txn.status === 'VOID' ? 'bg-gray-100 text-gray-600 line-through' :
                                            txn.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                                            txn.status === 'PENDING' ? 'bg-yellow-100 text-yellow-700' :
                                            'bg-blue-100 text-blue-700'
                                        }`}>
                                            {txn.status === 'VOID' ? 'Voided' : txn.status === 'COMPLETED' ? 'Completed' : txn.status === 'PENDING' ? 'Pending' : txn.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                        {txn.status !== 'VOID' && (
                                            <button
                                                onClick={() => setShowVoidDialog(txn)}
                                                className="text-xs text-red-600 hover:text-red-800 font-medium"
                                            >
                                                Void
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="9" className="px-6 py-12 text-center text-gray-500">
                                    No transactions found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* New Transaction Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl font-semibold text-gray-900">Record New Transaction</h2>
                                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Trust Account *</label>
                                    <select
                                        required
                                        value={formData.account}
                                        onChange={(e) => setFormData({ ...formData, account: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                    >
                                        <option value="">Select Account</option>
                                        {accounts.map(acc => (
                                            <option key={acc.id} value={acc.id}>{acc.account_name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Type *</label>
                                    <select
                                        required
                                        value={formData.transaction_type}
                                        onChange={(e) => setFormData({ ...formData, transaction_type: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                    >
                                        {transactionTypes.map(type => (
                                            <option key={type.value} value={type.value}>{type.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Client *</label>
                                    <select
                                        required
                                        value={formData.client}
                                        onChange={(e) => {
                                            setFormData({ ...formData, client: e.target.value, matter: '' });
                                            if (e.target.value) fetchMatters(e.target.value);
                                        }}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                    >
                                        <option value="">Select Client</option>
                                        {clients.map(client => (
                                            <option key={client.id} value={client.id}>
                                                {client.first_name} {client.last_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Matter (Optional)</label>
                                    <select
                                        value={formData.matter}
                                        onChange={(e) => setFormData({ ...formData, matter: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                        disabled={!formData.client}
                                    >
                                        <option value="">Select Matter</option>
                                        {matters.map(matter => (
                                            <option key={matter.id} value={matter.id}>{matter.title}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-2 text-gray-500">$</span>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0.01"
                                            required
                                            value={formData.amount}
                                            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                            className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2"
                                            placeholder="0.00"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Date *</label>
                                    <input
                                        type="date"
                                        required
                                        value={formData.transaction_date}
                                        onChange={(e) => setFormData({ ...formData, transaction_date: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Payee/Payor</label>
                                    <input
                                        type="text"
                                        value={formData.payee_payor}
                                        onChange={(e) => setFormData({ ...formData, payee_payor: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                        placeholder="Name of payee or payor"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Check Number</label>
                                    <input
                                        type="text"
                                        value={formData.check_number}
                                        onChange={(e) => setFormData({ ...formData, check_number: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                        placeholder="If applicable"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                                <textarea
                                    required
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                    rows="3"
                                    placeholder="Detailed description of the transaction"
                                ></textarea>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                    Record Transaction
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Void Confirmation Dialog */}
            {showVoidDialog && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowVoidDialog(null)}>
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6" onClick={e => e.stopPropagation()}>
                        <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 p-2 bg-red-100 rounded-full">
                                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">Void Transaction</h3>
                                <p className="mt-2 text-sm text-gray-600">
                                    Are you sure you want to void transaction <strong className="font-mono">{showVoidDialog.reference_number}</strong>?
                                    This will create a reversal entry of {formatCurrency(showVoidDialog.amount)} and cannot be undone.
                                </p>
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                onClick={() => setShowVoidDialog(null)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleVoidTransaction(showVoidDialog.id)}
                                disabled={voidingId === showVoidDialog.id}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                            >
                                {voidingId === showVoidDialog.id && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                                Void Transaction
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TrustTransactions;
